import { useEffect, useRef, useState, useCallback } from 'react';
import boneRushSprite from '../Moves/BoneRush.png';
import './BoneRushAnimation.css';

/**
 * BONE RUSH ANIMATION - Ground-type Move
 * Based on successful Karate Chop implementation
 * 
 * Animation Sequence:
 * 1. PREPARATION: Player stance preparation
 * 2. BONE_SPIN: frameIndex=0 spins towards target
 * 3. BONE_IMPACT: frameIndex=1 shows impact effect
 * 4. FADE_OUT: Clean disappearance
 * 
 * Sprite: BoneRush.png (384x192, 2 frames of 192x192 each)
 * - Frame 0: Spinning bone projectile
 * - Frame 1: Impact effect
 */
export default function BoneClubAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  const [animationPhase, setAnimationPhase] = useState('idle');

  const transitionTo = useCallback((phase) => {
    setAnimationPhase(phase);
  }, []);

  useEffect(() => {
    if (!playerRef?.current || !opponentRef?.current) return;

    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    const container = playerEl.closest('.battle-container') || document.body;

    // Get positioning data
    const p = playerEl.getBoundingClientRect();
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // SPRITE CONFIGURATION - BoneRush.png (384x192, 2 frames of 192x192 each)
    const SPRITE_CONFIG = {
      frameWidth: 192,   // Width of each frame (192x192)
      frameHeight: 192,  // Height of sprite
      spinFrame: 0,      // frameIndex=0 (spinning bone)
      impactFrame: 1,    // frameIndex=1 (impact effect)
    };

    // Calculate perfect center positioning over target
    const centerX = o.left - containerRect.left + o.width / 2;
    const centerY = o.top - containerRect.top + o.height / 2;

    // TIMING CONFIGURATION - Enhanced for spinning motion and impact
    const TIMING = {
      preparation: 120,    // Player stance preparation
      boneTravel: 200,     // Bone spinning towards target
      boneImpact: 300,     // frameIndex=1 impact duration
      fadeOut: 180,        // Clean fade out
    };

    console.log('🦴 Bone Rush Animation - Ground-type Move Starting');
    console.log(`🦴 Target center: (${centerX}, ${centerY})`);

    // PHASE 1: PREPARATION (0-120ms)
    setAnimationPhase('preparation');
    playerEl.style.transition = 'transform 120ms ease-in-out';
    playerEl.style.transform = 'scale(1.05) rotate(-3deg)'; // Ground-type stance
    
    // Subtle target highlight preparation
    opponentEl.style.transition = 'filter 120ms ease-in';
    opponentEl.style.filter = 'brightness(1.1)';
    
    // PHASE 2: SPINNING BONE TRAVEL - frameIndex=0 (120-320ms)
    const boneSpinTimer = setTimeout(() => {
      setAnimationPhase('bone_spin');
      console.log('🦴 Phase 2: Spinning Bone Travel (frameIndex=0)');
      
      // Create spinning bone effect - frameIndex=0
      const boneEffect = document.createElement('div');
      boneEffect.className = 'bone-rush-spin';
      
      // Start position: from player towards target with spinning motion
      const startX = p.left - containerRect.left + p.width / 2 - SPRITE_CONFIG.frameWidth / 2;
      const startY = p.top - containerRect.top + p.height / 2 - SPRITE_CONFIG.frameHeight / 2;
      const endX = centerX - SPRITE_CONFIG.frameWidth / 2;
      const endY = centerY - SPRITE_CONFIG.frameHeight / 2;
      
      boneEffect.style.left = `${startX}px`;
      boneEffect.style.top = `${startY}px`;
      boneEffect.style.width = `${SPRITE_CONFIG.frameWidth}px`;
      boneEffect.style.height = `${SPRITE_CONFIG.frameHeight}px`;
      
      // Set to frameIndex=0 (spinning bone)
      boneEffect.style.backgroundImage = `url(${boneRushSprite})`;
      boneEffect.style.backgroundSize = '384px 192px'; // Full sprite sheet
      boneEffect.style.backgroundPosition = '0px 0px'; // frameIndex=0 (first 192px)
      boneEffect.style.backgroundRepeat = 'no-repeat';
      boneEffect.style.position = 'absolute';
      boneEffect.style.zIndex = '90';
      boneEffect.style.pointerEvents = 'none';
      boneEffect.style.transition = 'left 200ms ease-out, top 200ms ease-out';
      
      container.appendChild(boneEffect);
      animRef.current = boneEffect;
      
      // Animate spinning bone travel to target
      setTimeout(() => {
        if (animRef.current) {
          animRef.current.style.left = `${endX}px`;
          animRef.current.style.top = `${endY}px`;
          console.log(`🦴 Bone spinning from (${startX}, ${startY}) to (${endX}, ${endY})`);
        }
      }, 30); // Slight delay for smooth travel start
      
    }, TIMING.preparation);
    
    // PHASE 3: BONE IMPACT - frameIndex=1 (320-620ms) - Extended visibility
    const boneImpactTimer = setTimeout(() => {
      setAnimationPhase('bone_impact');
      console.log('🦴 Phase 3: Bone Impact (frameIndex=1) - Extended Duration');
      
      if (animRef.current) {
        // Transition to frameIndex=1 with impact positioning
        animRef.current.style.backgroundPosition = '-192px 0px'; // frameIndex=1 (second 192px frame)
        animRef.current.className = 'bone-rush-impact';
        animRef.current.style.transition = 'none'; // Remove travel transition
        
        // Center the impact effect for better visibility
        const centerXPos = centerX - SPRITE_CONFIG.frameWidth / 2;
        const centerYPos = centerY - SPRITE_CONFIG.frameHeight / 2;
        animRef.current.style.left = `${centerXPos}px`;
        animRef.current.style.top = `${centerYPos}px`;
        
        console.log('🦴 Switched to frameIndex=1 (impact effect) at position -192px');
        
        // Impact flash effect
        const flashEffect = document.createElement('div');
        flashEffect.className = 'bone-rush-flash';
        flashEffect.style.left = `${centerX}px`;
        flashEffect.style.top = `${centerY}px`;
        container.appendChild(flashEffect);
        
        // Clean up flash after animation
        setTimeout(() => {
          if (flashEffect.parentNode) {
            flashEffect.parentNode.removeChild(flashEffect);
          }
        }, 250);
        
        // Opponent hit reaction
        opponentEl.classList.add('bone-rush-hit');
      }
      
    }, TIMING.preparation + TIMING.boneTravel);
    
    // PHASE 4: FADE OUT (620-800ms)
    const fadeOutTimer = setTimeout(() => {
      setAnimationPhase('fade_out');
      console.log('🦴 Phase 4: Fade Out');
      
      if (animRef.current) {
        animRef.current.className = 'bone-rush-fade-out';
      }
      
    }, TIMING.preparation + TIMING.boneTravel + TIMING.boneImpact);
    
    // PHASE 5: COMPLETE (800ms)
    const completeTimer = setTimeout(() => {
      setAnimationPhase('complete');
      console.log('🦴 Phase 5: Animation Complete - Ground-type Move');
      
      // Cleanup and reset
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Reset player and opponent states
      playerEl.style.transition = '';
      playerEl.style.transform = '';
      opponentEl.style.transition = '';
      opponentEl.style.filter = '';
      opponentEl.classList.remove('bone-rush-hit');
      
      onComplete();
      
    }, TIMING.preparation + TIMING.boneTravel + TIMING.boneImpact + TIMING.fadeOut);

    // Enhanced cleanup function
    return () => {
      clearTimeout(boneSpinTimer);
      clearTimeout(boneImpactTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(completeTimer);
      
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Reset states
      if (playerEl) {
        playerEl.style.transition = '';
        playerEl.style.transform = '';
      }
      if (opponentEl) {
        opponentEl.style.transition = '';
        opponentEl.style.filter = '';
        opponentEl.classList.remove('bone-rush-hit');
      }
      
      console.log('🦴 Bone Rush Animation: Cleanup completed');
    };
  }, [playerRef, opponentRef, onComplete, transitionTo]);

  return null; // This component only handles DOM manipulation
}
